import React, { useState } from "react";
import { Space, Typography, Card, Button, Input, Grid, notification } from "antd";
import { SearchOutlined, ReloadOutlined, QuestionCircleOutlined, CloseCircleOutlined, PlusOutlined, EditOutlined, ControlOutlined } from "@ant-design/icons";
import { CustomButtonComponent } from "../global/CustomButtonComponent";
import { ProviderTableComponent } from "./view/ProviderTableComponent";
import { ProviderModalComponent } from "./ProviderModalComponent";
import { CalculationSettingsListModalComponent } from "./calculationSettings/CalculationSettingsListModalComponent";
import { CreateProvider } from "../../api/CreateProvider";
import { UpdateProvider } from "../../api/UpdateProvider";

const { Text } = Typography;

type ProviderIndexComponentProps = {
  providersList: any;
  onPaginationChange: any;
  searchText: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  selectedRow: any;
  onSelectRow: (ids: Array<number>, records: Array<any>) => any;
};

export const ProviderIndexComponent = ({
  providersList,
  onPaginationChange,
  searchText,
  onSearchChange,
  onRefresh,
  selectedRow,
  onSelectRow,
}: ProviderIndexComponentProps) => {
  const screens = Grid.useBreakpoint();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const handleOpenCreateModal = () => {
    onSelectRow([], []); // Clear selection for create mode
    setIsModalOpen(true);
  };

  const handleOpenUpdateModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenSettingsModal = () => {
    setIsSettingsModalOpen(true);
  };

  const handleCloseSettingsModal = () => {
    setIsSettingsModalOpen(false);
  };

  const handleSaveProvider = async (payload: any) => {
    setLoading(true);
    try {
      let result;
      const isUpdate = !!selectedRow;

      if (isUpdate) {
        result = await UpdateProvider(selectedRow.provider_id, payload);
      } else {
        result = await CreateProvider(payload);
      }
      
      if (result && (result.status === 200 || result.status === 201 || result.status === 204)) {
        notification.success({
          message: 'Success',
          description: `Provider "${payload.provider_name}" has been ${isUpdate ? 'updated' : 'created'} successfully.`,
          placement: 'topRight'
        });

        // 1. Prepare the fresh data for the UI
        if (isUpdate) {
          const backendData = result.data?.data || {};
          // Merge logic: Existing -> Payload Values -> API Result
          const updatedRecord = {
            ...selectedRow,
            ...payload,
            ...backendData
          };

          // Priority for monthly_terms: New Payload -> API Result -> Existing
          // If payload has monthly_terms, it means they were changed.
          if (payload.monthly_terms) {
            updatedRecord.monthly_terms = payload.monthly_terms;
          } else if (!backendData.monthly_terms) {
            updatedRecord.monthly_terms = selectedRow.monthly_terms;
          }

          updatedRecord.key = updatedRecord.provider_id;
          
          // 2. Update selected row state in parent
          onSelectRow([updatedRecord.provider_id], [updatedRecord]);
        } else {
          onSelectRow([], []); 
        }

        // 3. Close and refresh
        setIsModalOpen(false);
        onRefresh();
      } else {
        throw new Error(result?.data?.message || `Failed to ${isUpdate ? 'update' : 'create'} provider`);
      }
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.message || 'An unexpected error occurred.',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "12px",
        }}
        bodyStyle={{ padding: "24px" }}
      >
        <div
          style={{
            background: "#f1f5f9",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <QuestionCircleOutlined style={{ color: "#1e293b", fontSize: "18px", marginTop: "2px" }} />
          <div>
            <Text strong style={{ display: "block", marginBottom: "4px" }}>Admin Guide</Text>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              Manage providers, search by name, and view more details about them.
            </Text>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Space size={12} direction={screens.xs ? "vertical" : "horizontal"} style={{ width: screens.xs ? "100%" : "auto" }}>
            <Input.Search
              placeholder="Search Provider data..."
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: screens.xs ? "100%" : 350 }}
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              onSearch={onSearchChange}
            />
            {selectedRow && (
              <CustomButtonComponent
                variant="secondary"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => onSelectRow([], [])}
                style={{ width: screens.xs ? "100%" : "auto" }}
              >
                Clear Selection
              </CustomButtonComponent>
            )}
            <CustomButtonComponent
              variant="secondary"
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              style={{ width: screens.xs ? "100%" : "auto" }}
            >
              Refresh
            </CustomButtonComponent>
          </Space>

          <Space size={12} direction={screens.xs ? "vertical" : "horizontal"} style={{ width: screens.xs ? "100%" : "auto" }}>
            {selectedRow && (
              <CustomButtonComponent
                variant="secondary"
                icon={<ControlOutlined />}
                onClick={handleOpenSettingsModal}
                style={{ width: screens.xs ? "100%" : "auto" }}
              >
                Calculation Settings
              </CustomButtonComponent>
            )}
            {selectedRow && (
              <CustomButtonComponent
                variant="secondary"
                icon={<EditOutlined />}
                onClick={handleOpenUpdateModal}
                style={{ width: screens.xs ? "100%" : "auto" }}
              >
                Update {selectedRow?.provider_name}
              </CustomButtonComponent>
            )}
            <CustomButtonComponent
              variant="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreateModal}
              style={{ width: screens.xs ? "100%" : "auto" }}
            >
              Create Provider
            </CustomButtonComponent>
          </Space>
        </div>

        <style>{`
          .premium-search {
            box-shadow: none !important;
          }

          .premium-search .ant-input-wrapper.ant-input-group {
            border: 1px solid #d6dee8 !important;
            border-radius: 8px !important;
            overflow: hidden !important;
            box-shadow: none !important;
          }
          
          .premium-search .ant-input {
            border: none !important;
            box-shadow: none !important;
          }

          .premium-search .ant-input-group-addon {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
            box-shadow: none !important;
          }

          .premium-search .ant-input-search-button {
            border: none !important;
            border-left: 1px solid #d6dee8 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            height: 100% !important;
          }
          
          .premium-search .ant-input-search-button:hover {
            background: #f1f5f9 !important;
            color: #1e293b !important;
            box-shadow: none !important;
          }
          
          .premium-search .ant-input-search-button:focus {
            box-shadow: none !important;
          }
        `}</style>

        <ProviderTableComponent
          tableProps={providersList}
          onPaginationChange={onPaginationChange}
          onSelectRow={onSelectRow}
          selectedRowKey={selectedRow ? selectedRow.key : null}
        />
      </Card>

      <ProviderModalComponent
        open={isModalOpen}
        record={selectedRow}
        onCancel={handleCloseModal}
        onSave={handleSaveProvider}
        loading={loading}
      />

      <CalculationSettingsListModalComponent
        open={isSettingsModalOpen}
        providerId={selectedRow?.provider_id}
        providerName={selectedRow?.provider_name}
        onCancel={handleCloseSettingsModal}
      />
    </div>
  );
};
